import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { T, glass } from '../pages/shared'

// ── Workflow steps ─────────────────────────────────────────────────────────────
// Each page maps to a step. Pages not in the flow get no bar (Dashboard, Settings, etc.)
const STEPS = [
  { id: 1, label: 'Project',       route: '/projects',   icon: '◈', short: 'Project'   },
  { id: 2, label: 'Upload Data',   route: '/datasets',   icon: '↑', short: 'Upload'    },
  { id: 3, label: 'Seed Rules',    route: '/rules',      icon: '⚙', short: 'Rules'     },
  { id: 4, label: 'Run DQA',       route: '/run',        icon: '▷', short: 'Run DQA'  },
  { id: 5, label: 'Violations',    route: '/violations', icon: '⚑', short: 'Violations' },
  { id: 6, label: 'Corrections',   route: '/corrections',icon: '⊿', short: 'Correct'   },
  { id: 7, label: 'Audit & Report',route: '/audit',      icon: '☰', short: 'Audit'     },
]

const FLOW_ROUTES = new Set(STEPS.map(s => s.route))

export default function ProcessFlow() {
  const nav = useNavigate()
  const loc = useLocation()

  // Only show on workflow pages
  if (!FLOW_ROUTES.has(loc.pathname)) return null

  const currentStep = STEPS.find(s => s.route === loc.pathname)?.id ?? 0

  return (
    <div style={{
      width: '100%',
      ...glass,
      padding: '12px 16px 14px',
      marginBottom: 18,
      borderRadius: 'var(--r-lg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {STEPS.map((step, idx) => {
          const isActive   = step.id === currentStep
          const isDone     = step.id < currentStep
          const isNext     = step.id === currentStep + 1
          const isLast     = idx === STEPS.length - 1

          return (
            <React.Fragment key={step.id}>
              {/* Step pill */}
              <button
                onClick={() => nav(step.route)}
                title={`Step ${step.id}: ${step.label}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 20,
                  border: isActive
                    ? `1.5px solid ${T.canyon}`
                    : isDone
                    ? `1.5px solid var(--olivine, #6C7F38)`
                    : `1.5px solid var(--border-2)`,
                  background: isActive
                    ? T.canyonDim
                    : isDone
                    ? 'rgba(122,140,46,0.10)'
                    : isNext
                    ? 'var(--surface-3)'
                    : 'transparent',
                  color: isActive
                    ? T.canyon
                    : isDone
                    ? 'var(--olivine, #6C7F38)'
                    : 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  backdropFilter: 'blur(12px) saturate(1.2)',
                  WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
                  boxShadow: isActive
                    ? `0 2px 14px ${T.canyonGlow}, inset 0 1px 0 rgba(255,255,255,0.22)`
                    : 'inset 0 1px 0 rgba(255,255,255,0.14)',
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = T.canyon
                    e.currentTarget.style.color = T.canyon
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = isDone ? 'var(--olivine, #6C7F38)' : 'var(--border-2)'
                    e.currentTarget.style.color = isDone ? 'var(--olivine, #6C7F38)' : 'var(--text-muted)'
                  }
                }}
              >
                {/* Step number / checkmark */}
                <span style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800, fontFamily: T.fontDisplay,
                  background: isActive
                    ? T.canyon
                    : isDone
                    ? 'var(--olivine, #6C7F38)'
                    : 'var(--surface-4)',
                  color: isActive || isDone ? '#fff' : 'var(--text-muted)',
                }}>
                  {isDone ? '✓' : step.id}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: isActive ? 700 : 500,
                  fontFamily: T.fontBody,
                }}>
                  {step.short}
                </span>
              </button>

              {/* Connector arrow */}
              {!isLast && (
                <div style={{
                  flex: 1, height: 1, minWidth: 8, maxWidth: 28,
                  background: isDone
                    ? 'var(--olivine, #6C7F38)'
                    : 'var(--border-2)',
                  opacity: isDone ? 0.6 : 0.4,
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', right: -4, top: -5,
                    fontSize: 10, color: isDone ? 'var(--olivine, #6C7F38)' : 'var(--border-2)',
                    opacity: isDone ? 0.8 : 0.5,
                  }}>›</span>
                </div>
              )}
            </React.Fragment>
          )
        })}

        {/* Next step hint */}
        {currentStep < STEPS.length && (
          <div style={{ marginLeft: 'auto', paddingLeft: 12, flexShrink: 0 }}>
            <button
              onClick={() => nav(STEPS[currentStep].route)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 20,
                border: 'none',
                background: `linear-gradient(135deg, ${T.canyon}, ${T.canyon80})`,
                color: T.offWhite, cursor: 'pointer',
                fontSize: 11, fontWeight: 700, fontFamily: T.fontBody,
                boxShadow: `0 4px 20px ${T.canyonGlow}, inset 0 1px 0 rgba(255,255,255,0.22)`,
                whiteSpace: 'nowrap',
              }}
            >
              Next: {STEPS[currentStep].label} →
            </button>
          </div>
        )}
      </div>

      {/* Current step description */}
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontFamily: T.fontBody }}>
        {currentStep === 1 && 'Step 1 of 7 — Create or select a project to group your datasets and rules'}
        {currentStep === 2 && 'Step 2 of 7 — Upload your STR1 or STR2 CSV/Excel sensor data file'}
        {currentStep === 3 && 'Step 3 of 7 — Select your project and click ⚙ Seed CO₂ Rules to load all 28 DQA rules'}
        {currentStep === 4 && 'Step 4 of 7 — Select project + dataset, then click ▷ Execute DQA Run and watch results appear live'}
        {currentStep === 5 && 'Step 5 of 7 — Review violations by dimension and severity, export or batch-send to Correction Workbench'}
        {currentStep === 6 && 'Step 6 of 7 — Generate suggestions, approve/reject/override, then Apply All to Production'}
        {currentStep === 7 && 'Step 7 of 7 — Review the immutable audit trail and export your compliance certificate'}
      </div>
    </div>
  )
}
